import { useTranslation } from "@sagot/i18n";
import { Segmented } from "@sagot/ui";
import { isConnectorCall, type AnyHttpRequestStep } from "../types";
import { blankInline, callFrom } from "./connector-op-model";
import { ConnectorForm } from "./connector-form";
import { OperationForm } from "./connector-op-form";
import type { FlowContext } from "./flow-context";
import type { FormProps } from "./webhook-form";

const C = "engage.workflows.connector.mode";

type Mode = "operation" | "inline";

/**
 * The `http_request` step has two forms: "Call connector operation" (a connector and one of its operations, SD-SC-12) and the
 * inline request to a webhook endpoint the data connector always had. A step that exists keeps its form; the switch starts the
 * other one over (the id, the name and the success / failure exits stay).
 */
export function ConnectorStepForm({ step, disabled, onChange, paths, canAddPath, facing = false }: FormProps<AnyHttpRequestStep> & Pick<FlowContext, "paths" | "canAddPath" | "facing">) {
  const { t } = useTranslation();
  const mode: Mode = isConnectorCall(step) ? "operation" : "inline";
  const choose = (next: Mode) => {
    if (next === mode || disabled) return;
    onChange(next === "operation" && !isConnectorCall(step) ? callFrom(step) : isConnectorCall(step) ? blankInline(step) : step);
  };
  return (
    <div className="space-y-5">
      <div data-testid="connector-mode">
        <p className="label">{t(`${C}.label`)}</p>
        <Segmented<Mode>
          aria-label={t(`${C}.label`)}
          value={mode}
          onValueChange={choose}
          options={[
            { value: "operation", label: t(`${C}.operation`) },
            { value: "inline", label: t(`${C}.inline`) },
          ]}
        />
        <p className="hint">{t(`${C}.${mode}Hint`)}</p>
      </div>
      {isConnectorCall(step) ? (
        <OperationForm step={step} disabled={disabled} onChange={onChange} paths={paths} canAddPath={canAddPath} facing={facing} />
      ) : (
        <ConnectorForm step={step} disabled={disabled} onChange={onChange} paths={paths} canAddPath={canAddPath} facing={facing} />
      )}
    </div>
  );
}
